import React, { useRef, useState } from 'react';
import { Upload, Download, X, Eye } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Colores por extensión de archivo
const getFileColor = (ext) => {
  const colors = {
    'PDF': 'bg-red-100 text-red-700 border-red-200',
    'XLSX': 'bg-green-100 text-green-700 border-green-200',
    'XLS': 'bg-green-100 text-green-700 border-green-200',
    'DOC': 'bg-blue-100 text-blue-700 border-blue-200',
    'DOCX': 'bg-blue-100 text-blue-700 border-blue-200',
    'DXF': 'bg-purple-100 text-purple-700 border-purple-200',
    'AI': 'bg-orange-100 text-orange-700 border-orange-200',
    'CDR': 'bg-pink-100 text-pink-700 border-pink-200',
    'PLT': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  };
  return colors[ext?.toUpperCase()] || 'bg-slate-100 text-slate-700 border-slate-200';
};

const getFileExtension = (filename) => {
  if (!filename) return '';
  return filename.split('.').pop()?.toUpperCase() || '';
};

// Extrae el nombre original del archivo desde el path
// Formato esperado: muestras/folder/uuid_originalname.ext
const getOriginalFileName = (filename) => {
  if (!filename) return '';
  const pathFilename = filename.split('/').pop() || '';
  // Si tiene formato uuid_name, extraer la parte del nombre
  if (pathFilename.includes('_')) {
    return pathFilename.split('_').slice(1).join('_');
  }
  return pathFilename;
};

const getFileUrl = (filename, forDownload = false) => {
  if (!filename) return null;
  const base = filename.startsWith('http') ? filename : `${API_BASE}/api/muestras/files/${filename}`;
  return forDownload ? `${base}?download=true` : base;
};

/**
 * FileCell - Celda con subida directa de archivos
 * @param {string} value - Nombre del archivo actual
 * @param {function} onUpload - Callback cuando se sube un archivo (file) => Promise
 * @param {function} onRemove - Callback cuando se elimina el archivo () => Promise
 * @param {string} accept - Tipos de archivo aceptados (default: ".pdf,.xlsx,.xls,.doc,.docx")
 * @param {boolean} disabled - Si está deshabilitado
 */
export const FileCell = ({ value, onUpload, onRemove, accept = ".pdf,.xlsx,.xls,.doc,.docx", disabled = false }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !onUpload) return;
    
    setUploading(true);
    try {
      await onUpload(file);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    if (!onRemove) return;
    try {
      await onRemove();
    } catch (error) {
      console.error('Error removing file:', error);
    }
  };

  const ext = getFileExtension(value);
  const colorClass = getFileColor(ext);

  if (!value) {
    return (
      <div className="flex items-center">
        <input 
          ref={inputRef} 
          type="file" 
          accept={accept} 
          onChange={handleFileChange} 
          className="hidden" 
          disabled={disabled || uploading}
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium 
            bg-slate-100 text-slate-600 hover:bg-blue-100 hover:text-blue-700 
            border border-dashed border-slate-300 hover:border-blue-400
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Upload size={12} className="mr-1" />
          {uploading ? 'Subiendo...' : 'Subir'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <a
        href={getFileUrl(value, true)}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${colorClass} hover:opacity-80 transition-opacity max-w-[140px]`}
        title={getOriginalFileName(value)}
      >
        <Download size={12} className="mr-1 flex-shrink-0" />
        <span className="truncate">{getOriginalFileName(value) || ext}</span>
      </a>
      {onRemove && !disabled && (
        <button
          onClick={handleRemove}
          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
          title="Quitar archivo"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

/**
 * ImageCell - Celda con imagen miniatura clickeable
 * @param {string} value - Nombre/URL de la imagen
 * @param {function} onUpload - Callback cuando se sube una imagen (file) => Promise
 * @param {function} onRemove - Callback cuando se elimina la imagen () => Promise
 * @param {boolean} disabled - Si está deshabilitado
 */
export const ImageCell = ({ value, onUpload, onRemove, disabled = false }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showViewer, setShowViewer] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !onUpload) return;
    
    setUploading(true);
    try {
      await onUpload(file);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const imageUrl = getFileUrl(value);

  if (!value) {
    return (
      <div className="flex items-center">
        <input 
          ref={inputRef} 
          type="file" 
          accept="image/*" 
          onChange={handleFileChange} 
          className="hidden" 
          disabled={disabled || uploading}
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-medium 
            bg-slate-100 text-slate-600 hover:bg-blue-100 hover:text-blue-700 
            border border-dashed border-slate-300 hover:border-blue-400
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Upload size={12} className="mr-1" />
          {uploading ? 'Subiendo...' : 'Subir'}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-1">
        <button 
          onClick={() => setShowViewer(true)}
          className="focus:outline-none"
        >
          <img 
            src={imageUrl}
            alt="Preview"
            className="w-16 h-16 object-cover rounded border border-slate-200 
              hover:border-blue-500 hover:scale-105 cursor-pointer transition-all"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </button>
        {onRemove && !disabled && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
            title="Quitar imagen"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Modal Visor de Imagen */}
      {showViewer && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowViewer(false)}
        >
          <div 
            className="relative bg-white rounded-xl p-2 max-w-4xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowViewer(false)}
              className="absolute -top-3 -right-3 p-2 bg-white rounded-full shadow-lg text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>
            <img 
              src={imageUrl}
              alt="Full view"
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-3 flex justify-center gap-2">
              <a
                href={getFileUrl(value, true)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                <Download size={16} className="mr-2" />
                Descargar
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * RentabilidadBadge - Badge de rentabilidad con colores
 * @param {number} costo - Costo estimado
 * @param {number} precio - Precio estimado
 */
export const RentabilidadBadge = ({ costo, precio }) => {
  if (!costo || !precio || costo === 0) {
    return <span className="text-gray-400">-</span>;
  }
  
  const rentabilidad = ((precio - costo) / costo) * 100;
  
  let colorClass = 'bg-red-100 text-red-700'; // < 10%
  if (rentabilidad >= 25) {
    colorClass = 'bg-green-100 text-green-700';
  } else if (rentabilidad >= 10) {
    colorClass = 'bg-yellow-100 text-yellow-700';
  }
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium ${colorClass}`}>
      {rentabilidad.toFixed(1)}%
    </span>
  );
};

/**
 * StatusBadge - Badge de estado (Aprobado/Pendiente)
 * @param {boolean|number} aprobado - Estado de aprobación
 */
export const StatusBadge = ({ aprobado }) => {
  if (aprobado === 1 || aprobado === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-medium">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        Aprobado
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
      Pendiente
    </span>
  );
};

export default { FileCell, ImageCell, RentabilidadBadge, StatusBadge, getFileColor, getFileExtension, getFileUrl, getOriginalFileName };
